<template>
  <div class="shift">
    <div v-if="loading && shiftList.length === 0">
      <i class="fa fa-spinner fa-spin fa-2x fa-fw text-center"/>
    </div>
    <div 
      v-if="!showStaffName" 
      class="shift-name">
      <p>{{ shiftName }}</p>
    </div>
    <div 
      v-if="!(loading && shiftList.length === 0)" 
      class="list-group">
      <div 
        v-for="s in shiftList" 
        :key="s.id" 
        class="list-group-item shift-item" 
        @click="selectShift(s)">
        <div v-if="showDayDate"><p class="date">{{ renderDate(s.time_open) }}</p></div>
        <div class="time-container">
          <div 
            :title="renderDateTime(s.time_open)" 
            class="time time-open">
            <span>{{ renderTime(s.time_open) }}</span>
          </div>
          <div v-if="!shiftSameDay(s.time_open, s.time_close)">
            <p class="date time-close-date">{{ renderDate(s.time_close) }}</p>
          </div>
          <div 
            :title="renderDateTime(s.time_close)" 
            class="time time-close">
            <span>{{ renderTime(s.time_close) }}</span>
          </div>
        </div>
        <div 
          v-if="showOutletName || showStaffName" 
          :title="owner(s)" 
          class="owner-container">
          <p class="owner-start">{{ ownerStart(s) }}</p>
          <p 
            v-if="ownerEnd(s).length > 0" 
            class="owner-end">{{ ownerEnd(s) }}</p>
        </div>
      </div>
      <div v-if="shiftList.length === 0">
        <small>No shifts available for the selected date range.</small>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
    props: {
        // eslint-disable-next-line vue/require-prop-types,vue/require-default-prop
        since: {},
        // eslint-disable-next-line vue/require-prop-types,vue/require-default-prop
        until: {},
    },

    data() {
        return {
            loading: true,
            shiftName: '',
            showOutletName: false,
            showStaffName: false,
            showDayDate: false,
        };
    },

    computed: {
        ...mapGetters('shifts', ['shiftList']),

        activeOutlet() {
            return this.$store.getters.activeOutlet;
        },

        dateFilter() {
            return {
                since: this.since,
                until: this.until,
            };
        },
    },

    watch: {
        activeOutlet() {
            this.getShiftList({ since: this.since, until: this.until });
        },

        dateFilter() {
            this.getShiftList({ since: this.since, until: this.until });
        },

        shiftList() {
            this.loading = false;
            this.updateShiftDisplay();
        },
    },

    created() {
        this.getShiftList({ since: this.since, until: this.until });
    },

    methods: {
        ...mapActions('shifts', ['getShiftList']),

        owner(s) {
            return this.showOutletName ? s.outlet_name : s.staff_name;
        },

        ownerStart(s) {
            let o = this.owner(s);

            if (o.length > 5) {
                return o.slice(0, o.length - 5);
            } else {
                return o;
            }
        },

        ownerEnd(s) {
            let o = this.owner(s);

            if (o.length > 5) {
                return o.slice(-5);
            } else {
                return '';
            }
        },

        selectShift(s) {
            if (this.showOutletName) {
                this.$store.commit('setActiveOutlet', s.outlet_id);
            }

            this.$parent.selectRange(
                moment(s.time_open),
                moment(s.time_close),
                false,
            );
            this.$parent.closePopup();
        },

        updateShiftDisplay() {
            if (this.shiftList.length <= 0) {
                return;
            }

            this.showDayDate = false;
            this.showOutletName = false;
            this.showStaffName = false;
            this.shiftName = this.shiftList[0].staff_name;

            let date = moment(this.shiftList[0].time_open);
            let outlet = this.shiftList[0].outlet_id;
            let staff = this.shiftList[0].staff_id;

            let startDate = moment(this.$store.getters.startDate);
            let endDate = moment(this.$store.getters.endDate);

            if (!startDate.isSame(endDate, 'day')) {
                this.showDayDate = true;
            }

            for (let i = 0; i < this.shiftList.length; i++) {
                let s = this.shiftList[i];

                if (!date.isSame(moment(s.time_open), 'day')) {
                    this.showDayDate = true;
                }

                if (outlet !== s.outlet_id) {
                    this.showOutletName = true;
                }

                if (staff !== s.staff_id) {
                    this.showStaffName = true;
                }
            }
        },

        shiftSameDay(s, e) {
            let start = moment(s);
            let end = moment(e);

            return start.isSame(end, 'day');
        },

        renderDate(d) {
            return moment(d).format('DD MMM');
        },

        renderTime(t) {
            return moment(t).format('hh:mm a');
        },

        renderDateTime(d) {
            return moment(d).format('DD MMM hh:mm a');
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.shift {
    display: flex;
    flex-direction: column;
    justify-content: center;

    %ellipsis {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .shift-name {
        text-align: center;

        p {
            @extend %ellipsis;
            font-size: 10px;
            margin-bottom: 5px;
            text-transform: uppercase;
        }
    }

    .shift-item {
        cursor: pointer;
        padding: 6px 8px;

        .date {
            font-size: 10px;
            margin-bottom: 4px;
            margin-top: 0;
            text-transform: uppercase;
        }

        .owner-container {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            font-size: 10px;
            justify-content: flex-start;
            text-transform: uppercase;
        }

        .owner-start {
            @extend %ellipsis;
            flex-shrink: 1;
            margin-bottom: 0;
            margin-top: 4px;
        }

        .owner-end {
            flex-basis: content;
            flex-grow: 0;
            flex-shrink: 0;
            margin-bottom: 0;
            margin-top: 4px;
            white-space: nowrap;
        }

        .time {
            font-size: smaller;

            &:before {
                border-color: inherit;
                border-radius: 50%;
                border-style: solid;
                border-width: 3px;
                content: '';
                float: left;
                height: 4px;
                position: relative;
                top: 5px;
                width: 4px;
            }

            span {
                margin-left: 4px;
            }
        }

        .time-open {
            border-color: #6887ff;
        }

        .time-close {
            border-color: #6cc788;
        }

        .time-close-date {
            margin-top: 4px;
        }

        &:hover {
            background-color: #eeeeee;
        }
    }
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/Shiftpicker.vue